import { useInfiniteQuery } from '@tanstack/react-query';
import { useCallback } from 'react';
import { useDebouncedValue } from '@/hooks/useDebounceValue';
import { useFetch } from '@/hooks/useFetch';
import type { GetClientListResponse } from '@/types/clientDetailsApiTypes';
import type { ClientCrmStatus } from '@/types/common';

export const CLIENTS_SEARCH_INFINITE_QUERY_KEY = 'clients-search-infinite';

type PageResult = GetClientListResponse & {
  nextCursor?: number;
};

interface UseClientsSearchInfiniteQueryProps {
  search: string;
  perPage?: number;
  withEmail?: boolean;
  agent_id?: number;
  status?: ClientCrmStatus;
  fallbackSearch?: string;
}

export const useClientsSearchInfiniteQuery = ({
  search,
  perPage = 15,
  withEmail,
  agent_id,
  status,
  fallbackSearch,
}: UseClientsSearchInfiniteQueryProps) => {
  const { doFetch } = useFetch<void, GetClientListResponse>();
  const debouncedSearch = useDebouncedValue(search, 400);
  const effectiveSearch = debouncedSearch.trim() || fallbackSearch?.trim() || '';

  const buildUrl = useCallback(
    (pageParam: number) => {
      const params = new URLSearchParams();
      if (effectiveSearch) params.append('search', effectiveSearch);
      params.append('per_page', String(perPage));
      params.append('page', String(pageParam));
      if (withEmail) params.append('hasEmail', 'true');
      if (agent_id != null) params.append('agent_id', String(agent_id));
      if (status) params.append('status', status);
      return `/api/clients/list?${params.toString()}`;
    },
    [effectiveSearch, perPage, withEmail, agent_id, status]
  );

  const fetchPage = async ({ pageParam }: { pageParam: number }): Promise<PageResult> => {
    const url = buildUrl(pageParam);
    const results = await doFetch(url);
    return {
      ...results,
      nextCursor: results.pagination?.last_page !== results.pagination?.current_page ? pageParam + 1 : undefined,
    };
  };

  const queryResult = useInfiniteQuery({
    queryKey: [CLIENTS_SEARCH_INFINITE_QUERY_KEY, effectiveSearch, perPage, withEmail, agent_id, status],
    queryFn: fetchPage,
    getNextPageParam: (lastPage) => lastPage.nextCursor,
    initialPageParam: 1,
  });

  const clients = queryResult.data?.pages.flatMap((page) => page.data ?? []) ?? [];
  const total = queryResult.data?.pages?.[0]?.pagination?.total ?? 0;

  return {
    ...queryResult,
    clients,
    total,
    debouncedSearch,
  };
};
